import { useState } from "react";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/app/AppLayout";
import useTier from "../hooks/useTier";
import api from "../lib/api";
import { track } from "../lib/analytics";
import { Sparkles, Zap, ShieldCheck } from "lucide-react";
import { buildTiers } from "../components/billing/tiers";
import PlanCard from "../components/billing/PlanCard";
import CancelConfirmModal, { UsageBar, Perk } from "../components/billing/BillingExtras";

export default function BillingPage() {
  const { t, i18n } = useTranslation();
  const TIERS = buildTiers(t);
  const { tier, tierLabel, assignedTierLabel, isPremium, premiumSource, usage, limits, subscriptionStatus, refresh, betaMode, trialActive, trialEndsAt } = useTier();
  const adminGranted = premiumSource === "admin";
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [error, setError] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalMessage, setPortalMessage] = useState("");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelResult, setCancelResult] = useState(null);

  const startCheckout = async (packageId) => {
    setLoadingPkg(packageId);
    setError("");
    track("subscription_checkout_started", { package_id: packageId });
    try {
      const { data } = await api.post("/billing/create-checkout-session", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      track("subscription_checkout_failed", { package_id: packageId, reason: e.response?.status || "network" });
      setError(e.response?.data?.detail || t("billing.errCheckout"));
      setLoadingPkg(null);
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    setPortalMessage("");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      if (data.url) {
        window.location.href = data.url;
      } else if (data.simulated) {
        setPortalMessage(data.message || t("billing.portalSimulated"));
        await refresh();
      }
    } catch (e) {
      setPortalMessage(e.response?.data?.detail || t("billing.errPortal"));
    }
    setPortalLoading(false);
  };

  const confirmCancel = async () => {
    setCancelBusy(true);
    setCancelResult(null);
    try {
      const { data } = await api.post("/billing/cancel");
      setCancelResult({ ok: true, mode: data.mode, periodEnd: data.period_end });
      await refresh();
      setTimeout(() => setShowCancelConfirm(false), 1800);
    } catch (e) {
      setCancelResult({ ok: false, error: e.response?.data?.detail || t("billing.errPortal") });
    }
    setCancelBusy(false);
  };

  const closeCancelModal = () => { setShowCancelConfirm(false); setCancelResult(null); };
  const currentTier = tier || "free";
  const showUsage = currentTier !== "pro" && currentTier !== "pro_plus";

  return (
    <AppLayout>
      <div className="px-8 py-8 max-w-7xl mx-auto" data-testid="billing-page">
        <div className="mb-8">
          <div className="label-tech mb-2">{t("billing.title")}</div>
          <h1 className="font-display font-bold text-3xl text-slate-100">{t("billing.unlockTitle")}</h1>
          <p className="text-sm text-slate-400 mt-2">
            {t("billing.currentPlan")} <span className={`font-mono-data uppercase ${isPremium ? "text-amber-400" : "text-blue-400"}`} data-testid="billing-current-tier">{tierLabel || currentTier}</span>
            {subscriptionStatus && ` · ${t(`billing.statusLabels.${subscriptionStatus}`, { defaultValue: subscriptionStatus })}`}
            {adminGranted && <span className="text-amber-300" data-testid="billing-admin-granted"> · {t("billing.adminGranted")}{assignedTierLabel ? ` (${assignedTierLabel})` : ""}</span>}
          </p>
        </div>

        {betaMode && (
          <div className="mb-8 rounded-lg border border-blue-500/40 bg-gradient-to-br from-blue-500/10 to-cyan-500/5 p-5" data-testid="billing-beta-notice">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-blue-500/20 flex items-center justify-center shrink-0">
                <Sparkles size={18} className="text-blue-300"/>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] label-tech px-2 py-0.5 rounded bg-blue-500/20 border border-blue-500/40 text-blue-200">{t("billing.beta.badge")}</span>
                  <div className="font-display font-bold text-slate-100">{t("billing.beta.title", { defaultValue: "Pagamenti temporaneamente disabilitati" })}</div>
                </div>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                  {t("billing.beta.body", { defaultValue: "Siamo in fase di beta test: tutti i piani sono sbloccati gratuitamente per 30 giorni così potrai testare ogni funzionalità in piena libertà. Riceverai una notifica appena i pagamenti verranno riattivati." })}
                </p>
                {trialActive && trialEndsAt && (
                  <p className="mt-2 text-xs text-blue-300 font-mono">
                    {t("billing.beta.trialActive", { defaultValue: "Trial attivo · scade il" })}{" "}
                    {new Date(trialEndsAt).toLocaleDateString(i18n.language, { day: "2-digit", month: "long", year: "numeric" })}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {showUsage && (
          <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <UsageBar label={t("billing.quotesThisMonth")} value={usage?.quotes_this_month || 0} max={limits?.quotes_per_month || 10}/>
            <UsageBar label={t("billing.aiInsightsToday")} value={usage?.ai_insights_today || 0} max={limits?.ai_insights_per_day || 1}/>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIERS.map((plan) => (
            <PlanCard
              key={plan.id} plan={plan} t={t}
              isCurrent={currentTier === plan.id}
              loadingPkg={loadingPkg}
              portalLoading={portalLoading}
              betaMode={betaMode}
              adminGranted={adminGranted}
              onStartCheckout={startCheckout}
              onOpenPortal={openPortal}
              onRequestCancel={() => setShowCancelConfirm(true)}
            />
          ))}
        </div>

        {error && <div className="mt-4 text-sm text-red-400" data-testid="checkout-error">{error}</div>}
        {portalMessage && <div className="mt-4 text-sm text-amber-300" data-testid="portal-message">{portalMessage}</div>}

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Perk icon={<Zap size={18}/>} title={t("billing.perks.secure.title")} body={t("billing.perks.secure.body")}/>
          <Perk icon={<ShieldCheck size={18}/>} title={t("billing.perks.cancel.title")} body={t("billing.perks.cancel.body")}/>
          <Perk icon={<Sparkles size={18}/>} title={t("billing.perks.ai.title")} body={t("billing.perks.ai.body")}/>
        </div>

        <CancelConfirmModal
          open={showCancelConfirm} t={t} busy={cancelBusy} result={cancelResult}
          onConfirm={confirmCancel} onClose={closeCancelModal}
        />
      </div>
    </AppLayout>
  );
}
